import React from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Typography, Box, IconButton, Tooltip, Alert,
    TableSortLabel
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { NoMaxWidthTooltip } from '../common/SharedTooltip';
import { colors as chartColors } from '../../utils/themeUtils';
import { useNavigate } from 'react-router-dom';



// --- Inline CellTooltipContent ---
const CellTooltipContent = ({ ratings, businessRating, category, subject }) => {
    if (!ratings || ratings.length === 0) return null;
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    ratings.forEach(r => { if (counts[r] !== undefined) counts[r]++; });
    const positiveColor = '#4CAF50';
    const negativeColor = '#F44336';
    const neutralColor = 'text.secondary';
    return (
        <Box sx={{ textAlign: 'left' }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Ratings:</Typography><br />
            {[1, 2, 3, 4, 5].map(star => {
                let countColor = neutralColor;
                if (counts[star] === 0) countColor = neutralColor;
                else if (businessRating !== null && businessRating !== undefined) {
                    if (star > businessRating) countColor = positiveColor;
                    else if (star < businessRating) countColor = negativeColor;
                }
                return (
                    <React.Fragment key={`tooltip-star-${star}-${category || ''}-${subject || ''}`}>
                        <Typography variant="caption">
                            <span style={{ color: neutralColor }}>{star}★: </span>
                            <span style={{ fontWeight: 'bold', color: countColor }}>{counts[star]}</span>
                        </Typography><br />
                    </React.Fragment>
                );
            })}
        </Box>
    );
};

// Constants for column widths
const CATEGORY_COL_WIDTH = '150px';
const SUBJECT_COL_WIDTH = '150px';
const TOTAL_COL_WIDTH = '100px';

// --- Cell background color logic (restored from SubjectMatrixPage.js) ---
const getCellBackgroundColor = (avgRating, businessRating, isTotalCell = false) => {
    const defaultColor = 'transparent';
    // Use subtle solid RGBA colors for background
    const positiveAlpha = isTotalCell ? 0.5 : 0.10;
    const negativeAlpha = isTotalCell ? 0.5 : 0.10;

    const positiveColor = `rgba(76, 175, 80, ${positiveAlpha})`; // Subtle green background
    const negativeColor = `rgba(244, 67, 54, ${negativeAlpha})`; // Subtle red background

    if (avgRating === null || avgRating === undefined || businessRating === null || businessRating === undefined) {
        return defaultColor;
    }

    // Cells >= average rating should be green, cells < average rating should be red
    if (avgRating >= businessRating) {
        return positiveColor; // Use subtle green
    } else {
        return negativeColor; // Use subtle red
    }
};

function TimelineMatrixTable({
    matrixRenderData,
    groupedMonths,
    expandedCategories,
    isSubjectColumnGlobal,
    averageBusinessRating,
    handleToggleCategory,
    monthMinWidth,
    timeframe,
    sortConfig,
    onSortRequest
}) {
    const navigate = useNavigate();
    const years = Object.keys(groupedMonths);
    const lastYear = years[years.length - 1];

    // Helper for cell click navigation
    const handleCellClick = ({ category, subject, month }) => {
        const params = new URLSearchParams();
        if (category) params.set('category', category);
        if (subject) params.set('subject_group', subject);
        if (month) params.set('month', month);
        navigate(`/reviews?${params.toString()}`);
    };

    return (
        <Table stickyHeader aria-label="subject rating matrix"
            sx={{
                '& th, & td': {
                    px: { xs: 1, sm: 1.5, md: 1 },
                    py: { xs: 1, sm: 1.25, md: 1 },
                    fontSize: { xs: '0.875rem', md: '1rem' }
                },
            }}
        >
            <TableHead>
                <TableRow>
                    <TableCell rowSpan={2} sx={{
                        fontWeight: 700, color: '#0A2540', borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
                        position: 'sticky', top: 0, left: 0, zIndex: 6,
                        backgroundColor: '#fff',
                        boxSizing: 'border-box',
                        width: CATEGORY_COL_WIDTH,
                        minWidth: CATEGORY_COL_WIDTH,
                        maxWidth: CATEGORY_COL_WIDTH
                    }}>Category</TableCell>
                    <TableCell rowSpan={2} sx={{
                        fontWeight: 700,
                        color: isSubjectColumnGlobal ? '#0A2540' : 'transparent',
                        borderBottom: isSubjectColumnGlobal ? '2px solid rgba(25, 118, 210, 0.1)' : 'none',
                        position: 'sticky', top: 0, left: CATEGORY_COL_WIDTH, zIndex: 5,
                        backgroundColor: '#fff',
                        boxSizing: 'border-box',
                        visibility: isSubjectColumnGlobal ? 'visible' : 'hidden',
                        width: isSubjectColumnGlobal ? SUBJECT_COL_WIDTH : '0px',
                        minWidth: isSubjectColumnGlobal ? SUBJECT_COL_WIDTH : '0px',
                        maxWidth: isSubjectColumnGlobal ? '300px' : '0px',
                        padding: isSubjectColumnGlobal ? { xs: 1, sm: 1.5, md: 1 } : 0,
                        borderLeft: isSubjectColumnGlobal ? '1px solid rgba(25, 118, 210, 0.08)' : 'none',
                        borderRight: isSubjectColumnGlobal ? '1px solid rgba(25, 118, 210, 0.08)' : 'none',
                    }}>{isSubjectColumnGlobal ? 'Subject Group' : ''}</TableCell>
                    {Object.keys(groupedMonths).map(year => (
                        <TableCell
                            key={`year-header-${year}`}
                            align="center"
                            colSpan={groupedMonths[year].length}
                            sx={{
                                fontWeight: 700, color: '#0A2540',
                                borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
                                borderRight: '1px solid rgba(25, 118, 210, 0.08)',
                                position: 'sticky', top: 0, zIndex: 3,
                                backgroundColor: '#fff'
                            }}
                        >
                            {year}
                        </TableCell>
                    ))}
                    <TableCell rowSpan={2} align="center" sx={{
                        fontWeight: 700, color: '#0A2540',
                        borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
                        borderLeft: '2px solid rgba(25, 118, 210, 0.1)',
                        width: TOTAL_COL_WIDTH,
                        minWidth: TOTAL_COL_WIDTH,
                        backgroundColor: '#fff',
                        cursor: 'pointer'
                    }}>
                        <Tooltip 
                            title="Most impactful subjects are selected and sorted by average rating. Click to change sort order."
                            placement="left"
                            arrow
                        >
                            <TableSortLabel
                                active={sortConfig.key === 'totalData.avg'}
                                direction={sortConfig.key === 'totalData.avg' ? sortConfig.direction : 'asc'}
                                onClick={() => onSortRequest('totalData.avg')}
                            >
                                Total
                                {sortConfig.key === 'totalData.avg' ? (
                                    <Box component="span" sx={{ position: 'absolute', top: -10, right: -20, opacity: 0 }}/>
                                ) : null}
                            </TableSortLabel>
                        </Tooltip>
                    </TableCell>
                </TableRow>
                <TableRow>
                    {Object.keys(groupedMonths).flatMap(year =>
                        groupedMonths[year].map(month => (
                            <TableCell
                                key={`month-header-${year}-${month}`}
                                align="center"
                                sx={{
                                    fontWeight: 700,
                                    color: '#0A2540',
                                    borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
                                    position: 'sticky',
                                    top: '42px',
                                    zIndex: 2,
                                    backgroundColor: '#fff',
                                    minWidth: monthMinWidth,
                                    borderRight: '1px solid rgba(25, 118, 210, 0.08)',
                                }}
                            >
                                {new Date(month + '-01').toLocaleDateString('en-US', { month: 'short' })}
                            </TableCell>
                        ))
                    )}
                    {/* Empty cell for alignment with Total sort label if needed, or adjust Total colSpan in header */}
                     {/* This empty cell is for the second row of the header, under the Total column header, to ensure alignment */}
                     <TableCell sx={{ 
                        position: 'sticky', 
                        top: '42px', 
                        zIndex: 2, 
                        backgroundColor: '#fff', 
                        borderBottom: '2px solid rgba(25, 118, 210, 0.1)', // Match other header cells
                        borderLeft: '2px solid rgba(25, 118, 210, 0.1)', // Match Total header cell
                    }} />
                </TableRow>
            </TableHead>
            <TableBody>
                {matrixRenderData.map((row, rowIdx) => {
                    const { type, category, subject, monthData, totalData, id } = row;
                    const categoryRowKey = id;
                    const categoryExpansionState = expandedCategories[category];
                    const isExpanded = !!categoryExpansionState?.subjects;
                    const isLoadingSubjects = categoryExpansionState?.isLoading;
                    const subjectLoadError = categoryExpansionState?.error;
                    const subjects = categoryExpansionState?.subjects || [];
                    const years = Object.keys(groupedMonths);
                    // const lastYear = years[years.length - 1]; // Keep if used below, otherwise remove

                    // Helper function to render the category name cell
                    const renderCategoryCell = (isFirstSubjectRow = false) => (
                        <TableCell
                            component="th"
                            scope="row"
                            // Row span is 1 if not expanded OR it's an expanded category row itself (which we are hiding)
                            // If it's the first subject row of an expanded category, it spans all subjects.
                            rowSpan={isExpanded && subjects.length > 0 && isFirstSubjectRow ? subjects.length : 1}
                            sx={{
                                fontWeight: 'bold',
                                position: 'sticky',
                                left: 0,
                                background: (isExpanded && subjects.length > 0 && isFirstSubjectRow) || !isExpanded 
                                    ? 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)' 
                                    : 'transparent', // Should be transparent if it's a placeholder in a hidden category row concept
                                zIndex: 4, // High zIndex for sticky category name
                                boxSizing: 'border-box',
                                minWidth: CATEGORY_COL_WIDTH,
                                maxWidth: CATEGORY_COL_WIDTH,
                                width: CATEGORY_COL_WIDTH,
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                borderRight: '1px solid rgba(25, 118, 210, 0.08)',
                                cursor: 'pointer',
                                verticalAlign: 'top',
                                // No bottom border if it spans multiple subject rows from the first subject row
                                borderBottom: (isExpanded && subjects.length > 0 && isFirstSubjectRow) 
                                    ? 'none' 
                                    : '1px solid rgba(25, 118, 210, 0.08)',
                            }}
                            onClick={() => handleToggleCategory(category)}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconButton size="small" sx={{ p: 0.25 }}>
                                    {isLoadingSubjects ? (
                                        <CircularProgress size={18} />
                                    ) : isExpanded ? (
                                        <KeyboardArrowDownIcon />
                                    ) : (
                                        <KeyboardArrowRightIcon />
                                    )}
                                </IconButton>
                                <Tooltip 
                                    title={categoryExpansionState?.isLimited ? `Showing top ${subjects.length} subjects by impact (${categoryExpansionState.totalSubjects} total found). Selected for relevance across all locations.` : ''}
                                    placement="right"
                                    arrow
                                    disableHoverListener={!categoryExpansionState?.isLimited}
                                >
                                    <span style={{ cursor: categoryExpansionState?.isLimited ? 'help' : 'default' }}>
                                        {category}
                                        {categoryExpansionState?.isLimited && ` (top ${subjects.length})`}
                                    </span>
                                </Tooltip>
                            </Box>
                        </TableCell>
                    );

                    // Conditional rendering:
                    // If category is expanded AND has subjects, render subjects with the first one carrying the category cell
                    if (isExpanded && subjects.length > 0) {
                        return (
                            <React.Fragment key={`${categoryRowKey}-expanded`}>
                                {subjectLoadError && (
                                    <TableRow key={`${categoryRowKey}-error`}>
                                        <TableCell 
                                            colSpan={
                                                1 + // Category
                                                (isSubjectColumnGlobal ? 1 : 0) + // Subject
                                                Object.values(groupedMonths).reduce((acc, months) => acc + months.length, 0) + // Months
                                                1 // Total
                                            }
                                            sx={{ textAlign: 'center' }}
                                        >
                                            <Alert severity="error" sx={{ justifyContent: 'center' }}>
                                                Error loading subjects: {subjectLoadError.message || String(subjectLoadError)}
                                            </Alert>
                                        </TableCell>
                                    </TableRow>
                                )}

                                {subjects.map((subjectRow, subjIdx) => {
                                    const { subject: currentSubject, monthData: subjectMonthData, totalData: subjectTotalData, id: subjectId } = subjectRow;
                                    const subjectRowDisplayKey = subjectId ? `${categoryRowKey}-subj-${subjectId}` : `${categoryRowKey}-subj-${subjIdx}`;

                                    return (
                                        <TableRow 
                                            key={subjectRowDisplayKey}
                                            sx={{
                                                '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.03)' },
                                                // Ensure all cells in subject rows have a bottom border
                                                '& td, & th': { borderBottom: '1px solid rgba(25, 118, 210, 0.06)' },
                                                // If this is the last subject row, its bottom border acts as the category group's bottom border
                                                ...(subjIdx === subjects.length - 1 && {
                                                    '& td, & th': { borderBottom: '1px solid rgba(25, 118, 210, 0.08)' }, // Stronger border for group end
                                                }),
                                                backgroundColor: 'transparent', // Subjects rows are plain
                                            }}
                                        >
                                            {subjIdx === 0 && renderCategoryCell(true)} {/* Render category cell only for the first subject */}
                                            
                                            {/* Subject Group Cell for subject rows */}
                                            {isSubjectColumnGlobal && (
                                                <TableCell component="th" scope="row" sx={{
                                                    position: 'sticky',
                                                    left: CATEGORY_COL_WIDTH, 
                                                    background: 'linear-gradient(135deg, #eef6ff 0%, #f8f9fe 100%)',
                                                    zIndex: 3, // Lower than category cell, higher than month cells
                                                    boxSizing: 'border-box',
                                                    width: SUBJECT_COL_WIDTH,
                                                    minWidth: SUBJECT_COL_WIDTH,
                                                    maxWidth: '300px', // Kept from original
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                    fontWeight: 500,
                                                    color: '#0A2540',
                                                    pl: 2,
                                                    borderRight: '1px solid rgba(25, 118, 210, 0.08)',
                                                    // borderBottom is handled by TableRow sx
                                                }}>
                                                    <Tooltip title={currentSubject} placement="right" arrow>
                                                        <span>{currentSubject}</span>
                                                    </Tooltip>
                                                </TableCell>
                                            )}
                                            {/* If subject column is globally hidden but we are rendering subject rows, 
                                                we might need a placeholder or adjust colspan if the subject cell is not rendered.
                                                For now, assume if isSubjectColumnGlobal is false, this cell just doesn't render.
                                                The colSpan on error row above should be dynamic.
                                            */}
                                             {!isSubjectColumnGlobal && (subjIdx === 0 ? <TableCell sx={{ display:'none' }} />: null) }


                                            {/* Month Data Cells for subject rows */}
                                            {Object.keys(groupedMonths).flatMap(year =>
                                                groupedMonths[year].map(month => {
                                                    const cellKey = `${subjectRowDisplayKey}-${year}-${month}`;
                                                    const cellData = subjectMonthData ? subjectMonthData[month] : {}; // Ensure subjectMonthData exists
                                                    const avgRating = cellData?.avg;
                                                    const count = cellData?.count;
                                                    const ratings = cellData?.ratings;
                                                    let displayValue = null;
                                                    if (avgRating !== undefined && avgRating !== null) {
                                                        if (avgRating % 1 === 0) {
                                                            displayValue = avgRating.toFixed(0);
                                                        } else {
                                                            const twoDecimals = avgRating.toFixed(2);
                                                            displayValue = twoDecimals.endsWith('0') ? avgRating.toFixed(1) : twoDecimals;
                                                        }
                                                    }
                                                    const cellContent = (cellData && count > 0) ? (
                                                        <NoMaxWidthTooltip
                                                            title={<CellTooltipContent ratings={ratings} businessRating={averageBusinessRating} category={category} subject={currentSubject} />}
                                                            placement="top"
                                                            arrow
                                                        >
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', alignSelf: 'flex-start', pl: 0.5, pt: 0.25 }}>
                                                                    <VisibilityIcon sx={{ fontSize: '0.8rem', color: 'text.secondary', mr: 0.25 }} />
                                                                    <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1, fontSize: { xs: '0.875rem' } }}>
                                                                        {count.toLocaleString()}
                                                                    </Typography>
                                                                </Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', fontSize: { xs: '0.875rem', md: '1rem' }, py: 0.5, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                    {displayValue ?? ''} {displayValue ? ' ★' : ''}
                                                                </Typography>
                                                            </Box>
                                                        </NoMaxWidthTooltip>
                                                    ) :  <Box sx={{ height: '60px' }}/>; // Ensure consistent height
                                                    return (
                                                        <TableCell 
                                                            key={cellKey} 
                                                            align="center" 
                                                            sx={{ backgroundColor: getCellBackgroundColor(avgRating, averageBusinessRating, false), cursor: (cellData && count > 0 ? 'pointer' : 'default'), p:0.5 }}
                                                            onClick={cellData && count > 0 ? () => handleCellClick({ category, subject: currentSubject, month }) : undefined}
                                                        >
                                                            {cellContent}
                                                        </TableCell>
                                                    );
                                                })
                                            )}

                                            {/* Total Column Cell for subject rows */}
                                            {(() => {
                                                const totalKey = `${subjectRowDisplayKey}-total`;
                                                const totalAvgRating = subjectTotalData?.avg;
                                                const totalCount = subjectTotalData?.count;
                                                const totalRatings = subjectTotalData?.ratings;
                                                let totalDisplayValue = null;
                                                if (totalAvgRating !== undefined && totalAvgRating !== null) {
                                                    if (totalAvgRating % 1 === 0) {
                                                        totalDisplayValue = totalAvgRating.toFixed(0);
                                                    } else {
                                                        const twoDecimals = totalAvgRating.toFixed(2);
                                                        totalDisplayValue = twoDecimals.endsWith('0') ? totalAvgRating.toFixed(1) : twoDecimals;
                                                    }
                                                }
                                                const totalCellContent = totalData && totalCount > 0 ? ( // Should be subjectTotalData
                                                    <NoMaxWidthTooltip
                                                        title={<CellTooltipContent ratings={totalRatings} businessRating={averageBusinessRating} category={category} subject={currentSubject} />}
                                                        placement="left"
                                                        arrow
                                                    >
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', alignSelf: 'flex-start', pl: 0.5, pt: 0.25 }}>
                                                                <VisibilityIcon sx={{ fontSize: '0.8rem', color: 'text.secondary', mr: 0.25 }} />
                                                                <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1, fontSize: { xs: '0.875rem' } }}>
                                                                    {totalCount.toLocaleString()}
                                                                </Typography>
                                                            </Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', fontSize: { xs: '0.875rem', md: '1rem' }, py: 0.5, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                {totalDisplayValue ?? ''} {totalDisplayValue ? ' ★' : ''}
                                                            </Typography>
                                                        </Box>
                                                    </NoMaxWidthTooltip>
                                                ) : <Box sx={{ height: '60px' }}/>; // Ensure consistent height
                                                return (
                                                    <TableCell 
                                                        key={totalKey} 
                                                        align="center" 
                                                        sx={{ backgroundColor: getCellBackgroundColor(totalAvgRating, averageBusinessRating, true), cursor: (subjectTotalData && totalCount > 0 ? 'pointer' : 'default'), p:0.5 }}
                                                        onClick={subjectTotalData && totalCount > 0 ? () => handleCellClick({ category, subject: currentSubject, month: null }) : undefined}
                                                    >
                                                        {totalCellContent}
                                                    </TableCell>
                                                );
                                            })()}
                                        </TableRow>
                                    );
                                })}
                            </React.Fragment>
                        );
                    } else {
                        // Render the original category row (not expanded, or expanded but no subjects/error)
                        // This is the case for a collapsed category row, or an expanded one with no subjects to show.
                        return (
                            <TableRow 
                                key={categoryRowKey}
                                sx={{
                                    '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' },
                                    '& td, & th': { borderBottom: '1px solid rgba(25, 118, 210, 0.08)' },
                                    backgroundColor: isExpanded && subjects.length === 0 && !isLoadingSubjects && !subjectLoadError 
                                        ? 'rgba(25, 118, 210, 0.01)' // Slightly different background if expanded but empty
                                        : 'transparent',
                                }}
                            >
                                {renderCategoryCell(false)} {/* Category Cell, not spanning as it's the category row itself */}
                                
                                {/* Subject Group Placeholder Cell for unexpanded/empty-expanded category row */}
                                <TableCell sx={{
                                    position: 'sticky',
                                    left: CATEGORY_COL_WIDTH,
                                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)', // Match category cell style
                                    zIndex: 3,
                                    boxSizing: 'border-box',
                                    width: isSubjectColumnGlobal ? SUBJECT_COL_WIDTH : '0px',
                                    minWidth: isSubjectColumnGlobal ? SUBJECT_COL_WIDTH : '0px',
                                    padding: isSubjectColumnGlobal ? { xs: 1, sm: 1.5, md: 1 } : 0, // Standard padding
                                    borderRight: isSubjectColumnGlobal ? '1px solid rgba(25, 118, 210, 0.08)' : 'none',
                                    visibility: isSubjectColumnGlobal ? 'visible' : 'hidden',
                                    // borderBottom is handled by TableRow sx
                                }}>
                                    {/* Empty placeholder, content would be for subject if this row was a subject row */}
                                </TableCell>

                                {/* Month Data Cells for category row */}
                                {Object.keys(groupedMonths).flatMap(year =>
                                    groupedMonths[year].map(month => {
                                        const cellKey = `${categoryRowKey}-${year}-${month}`;
                                        const cellData = monthData[month]; // monthData from the original 'row'
                                        const avgRating = cellData?.avg;
                                        const count = cellData?.count;
                                        const ratings = cellData?.ratings;
                                        let displayValue = null;
                                        if (avgRating !== undefined && avgRating !== null) {
                                            if (avgRating % 1 === 0) {
                                                displayValue = avgRating.toFixed(0);
                                            } else {
                                                const twoDecimals = avgRating.toFixed(2);
                                                displayValue = twoDecimals.endsWith('0') ? avgRating.toFixed(1) : twoDecimals;
                                            }
                                        }
                                        const cellContent = (cellData && count > 0) ? (
                                            <NoMaxWidthTooltip
                                                title={<CellTooltipContent ratings={ratings} businessRating={averageBusinessRating} category={category} subject={null} />}
                                                placement="top"
                                                arrow
                                            >
                                                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', alignSelf: 'flex-start', pl: 0.5, pt: 0.25 }}>
                                                        <VisibilityIcon sx={{ fontSize: '0.8rem', color: 'text.secondary', mr: 0.25 }} />
                                                        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1, fontSize: { xs: '0.875rem' } }}>
                                                            {count.toLocaleString()}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', fontSize: { xs: '0.875rem', md: '1rem' }, py: 0.5, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        {displayValue ?? ''} {displayValue ? ' ★' : ''}
                                                    </Typography>
                                                </Box>
                                            </NoMaxWidthTooltip>
                                        ) : <Box sx={{ height: '60px' }}/>; // Ensure consistent height
                                        return (
                                            <TableCell 
                                                key={cellKey} 
                                                align="center" 
                                                sx={{ backgroundColor: getCellBackgroundColor(avgRating, averageBusinessRating, false), cursor: (cellData && count > 0 ? 'pointer' : 'default'), p:0.5 }}
                                                onClick={cellData && count > 0 ? () => handleCellClick({ category, subject: null, month }) : undefined}
                                            >
                                                {cellContent}
                                            </TableCell>
                                        );
                                    })
                                )}

                                {/* Total Column Cell for Category Row */}
                                {(() => {
                                    const totalKey = `${categoryRowKey}-total`;
                                    const totalAvgRating = totalData?.avg; // totalData from the original 'row'
                                    const totalCount = totalData?.count;
                                    const totalRatings = totalData?.ratings;
                                    let totalDisplayValue = null;
                                    if (totalAvgRating !== undefined && totalAvgRating !== null) {
                                        if (totalAvgRating % 1 === 0) {
                                            totalDisplayValue = totalAvgRating.toFixed(0);
                                        } else {
                                            const twoDecimals = totalAvgRating.toFixed(2);
                                            totalDisplayValue = twoDecimals.endsWith('0') ? totalAvgRating.toFixed(1) : twoDecimals;
                                        }
                                    }
                                    const totalCellContent = totalData && totalCount > 0 ? (
                                        <NoMaxWidthTooltip
                                            title={<CellTooltipContent ratings={totalRatings} businessRating={averageBusinessRating} category={category} subject={null} />}
                                            placement="left"
                                            arrow
                                        >
                                            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', alignSelf: 'flex-start', pl: 0.5, pt: 0.25 }}>
                                                    <VisibilityIcon sx={{ fontSize: '0.8rem', color: 'text.secondary', mr: 0.25 }} />
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1, fontSize: { xs: '0.875rem' } }}>
                                                        {totalCount.toLocaleString()}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', fontSize: { xs: '0.875rem', md: '1rem' }, py: 0.5, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    {totalDisplayValue ?? ''} {totalDisplayValue ? ' ★' : ''}
                                                </Typography>
                                            </Box>
                                        </NoMaxWidthTooltip>
                                    ) : <Box sx={{ height: '60px' }}/>; // Ensure consistent height
                                    return (
                                        <TableCell 
                                            key={totalKey} 
                                            align="center" 
                                            sx={{ backgroundColor: getCellBackgroundColor(totalAvgRating, averageBusinessRating, true), cursor: (totalData && totalCount > 0 ? 'pointer' : 'default'), p:0.5 }}
                                            onClick={totalData && totalCount > 0 ? () => handleCellClick({ category, subject: null, month: null }) : undefined}
                                        >
                                            {totalCellContent}
                                        </TableCell>
                                    );
                                })()}
                            </TableRow>
                        );
                    }
                })}
            </TableBody>
        </Table>
    );
}

export default TimelineMatrixTable; 